import enums from '@/mixins/enums';
import { areCodesValid } from '@/utils/validations';

export default {
  mixins: [enums],
  computed: {
    insuredPersons() { return this.model.insuredPersons.array_hd7ov6$_0; },
    adults() { return this.insuredPersons.filter((person) => person.isAdult); },
    children() { return this.insuredPersons.filter((person) => !person.isAdult); },
    /*
    * Returns array of persons who have to upload files.
    * */
    personsWithRequiredAttachments() {
      if (this.model.step < 17) return [];

      const { getRequiredAttachmentTypes_9lplti$ } = this.$kotlin.attachments;
      return this.insuredPersons
        .map((person, index) => {
          const { array_hd7ov6$_0 } = getRequiredAttachmentTypes_9lplti$(person, this.model);
          return { requiredAttachments: array_hd7ov6$_0, person, personIndex: index };
        })
        .filter((personWithAttachment) => personWithAttachment.requiredAttachments?.length);
    },
    personsWithRequiredAttachmentsForCourier() {
      if (this.model.step < 18) return [];

      const { getRequiredAttachmentTypesForCourier_amxqw8$ } = this.$kotlin.attachments;
      const { array_hd7ov6$_0 } = getRequiredAttachmentTypesForCourier_amxqw8$(this.model);
      const personIndex = this.adultsWithIndexes.length ? this.adultsWithIndexes[0].index : 0;
      if (array_hd7ov6$_0.length) {
        return [{ requiredAttachments: array_hd7ov6$_0, personIndex }];
      }
      return [];
    },
    /*
    * Returns array of adults and theirs real indexes.
    * { adult: InsuredPerson, index: Number }
    * */
    adultsWithIndexes() {
      return this.insuredPersons
        .map((person, index) => ({ adult: person, index }))
        .filter((personWithIndex) => personWithIndex.adult.isAdult);
    },
    /*
    * Returns array of children and theirs real indexes.
    * { child: InsuredPerson, index: Number }
    * */
    childrenWithIndexes() {
      return this.insuredPersons
        .map((person, index) => ({ child: person, index }))
        .filter((personWithIndex) => !personWithIndex.child.isAdult);
    },
    /*
    * Returns true only if children under the age of 6 are insured
    * */
    onlyChildrenUnder6years() {
      return this.insuredPersons.every((person, index) => {
        const birthNumberValid = areCodesValid(['V018', 'V019'].map((code) => `${code}_${index}`), this.errors);
        const birthDateValid = areCodesValid(['V022', 'V023'].map((code) => `${code}_${index}`), this.errors);
        if (!birthDateValid || !birthNumberValid) return false;
        const { yearDiffExact } = this.$kotlin.helpers.DateHelper;
        return yearDiffExact(person.birthDate, this.model.now) < 6;
      });
    },
    /*
    * Returns count of all adults in application also legal representatives are involved.
    * */
    countAllAdultPersons() {
      return [...new Set(this.insuredPersons
        .map((person) => {
          const { RelationshipEnum } = this.enums;
          const { isAdult, firstLegalRepresentative, birthNumber } = person;
          if (isAdult) return birthNumber;
          const { personIndex, birthNumber: LPBirthNumber, relationship } = firstLegalRepresentative;
          if (personIndex === -1 && relationship !== RelationshipEnum.PO) return LPBirthNumber;
          if (personIndex && personIndex !== -1) {
            const adultPerson = this.insuredPersons[personIndex];
            return adultPerson.birthNumber;
          }
          return null;
        })
        .filter((birthNumber) => birthNumber))].length;
    },
    onlyChildrenAndMaxOneRepresentativeAsInsuredPersons() {
      const adultInsuredPersons = this.adultsWithIndexes;
      const childrenInsuredPersons = this.childrenWithIndexes;
      if (adultInsuredPersons.length > 1) {
        return false;
      }
      if (adultInsuredPersons.length === 0) {
        return true;
      }
      return childrenInsuredPersons
        .filter((child) => child.child.firstLegalRepresentative.personIndex === adultInsuredPersons[0].index)
        .length > 0;
    },
    representativesOnlyParents() {
      const { RelationshipEnum } = this.enums;
      return this.childrenWithIndexes
        .filter((child) => child.child.firstLegalRepresentative.relationship !== RelationshipEnum.PARENT)
        .length === 0;
    },
  },
  methods: {
    /*
    * Method will set default countries for addresses and nationality for person.
    * Default counties are difference based on application language.
    * */
    setPersonCountries(person) {
      const slovakia = this.$store.getters.getSlovakia;

      if (slovakia) {
        person.postalAddress.country = slovakia;
        person.temporaryResidence.country = slovakia;
        person.firstLegalRepresentative.postalAddress.country = slovakia;
        person.firstLegalRepresentative.temporaryResidence.country = slovakia;
        person.secondLegalRepresentative.nationality = slovakia;
        person.nationality = slovakia;
        person.address.country = slovakia;
        person.firstLegalRepresentative.nationality = slovakia;
        person.firstLegalRepresentative.address.country = slovakia;
      }
    },
  },
};
